import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Header } from "@/pages/CollegePredictorPage";
import { GitCompare, Plus, X, Check } from "lucide-react";

const PARAMS = [
  { key: "nirf_rank", label: "NIRF Ranking", fmt: (v) => `#${v}`, better: "low" },
  { key: "fees", label: "Total Fees", fmt: (v) => `₹${(v / 100000).toFixed(1)}L`, better: "low" },
  { key: "placement_pct", label: "Placements", fmt: (v) => `${v}%`, better: "high" },
  { key: "avg_package", label: "Avg Package", fmt: (v) => `₹${(v / 100000).toFixed(1)}L`, better: "high" },
  { key: "highest_package", label: "Highest Package", fmt: (v) => `₹${(v / 100000).toFixed(1)}L`, better: "high" },
  { key: "faculty_rating", label: "Faculty", fmt: (v) => `${v}/5`, better: "high" },
  { key: "infra_rating", label: "Infrastructure", fmt: (v) => `${v}/5`, better: "high" },
  { key: "accreditation", label: "Accreditation", fmt: (v) => v, better: null },
  { key: "reviews_rating", label: "Student Reviews", fmt: (v) => `${v}/5`, better: "high" },
  { key: "roi_score", label: "ROI Score", fmt: (v) => `${v}/100`, better: "high" },
];

export default function CollegeComparePage() {
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState([]);
  const [compared, setCompared] = useState(null);

  useEffect(() => { api.get("/colleges").then((r) => setAll(r.data)).catch(() => {}); }, []);

  const toggle = (c) => {
    if (selected.find((s) => s.id === c.id)) setSelected(selected.filter((s) => s.id !== c.id));
    else if (selected.length >= 3) toast.info("Compare up to 3 colleges at a time.");
    else setSelected([...selected, c]);
  };

  const compare = async () => {
    if (selected.length < 2) { toast.info("Select at least 2 colleges."); return; }
    const { data } = await api.post("/colleges/compare", { college_ids: selected.map((s) => s.id) });
    setCompared(data);
  };

  const best = (key, better) => {
    if (!compared || !better) return null;
    const vals = compared.map((c) => c[key]);
    return better === "high" ? Math.max(...vals) : Math.min(...vals);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-8 py-8">
      <Header icon={GitCompare} eyebrow="College Comparison"
        title="Compare colleges side-by-side" desc="Select 2–3 colleges to compare across 10 key parameters." />

      {/* Selector */}
      <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-400">{selected.length} selected (max 3)</p>
          <button data-testid="compare-run-btn" onClick={compare} disabled={selected.length < 2}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2 text-sm font-bold text-[#041016] transition-transform hover:scale-105 disabled:opacity-40">
            <GitCompare className="h-4 w-4" /> Compare
          </button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {all.map((c) => {
            const sel = selected.find((s) => s.id === c.id);
            return (
              <button key={c.id} data-testid="compare-add-college-btn" onClick={() => toggle(c)}
                className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-all ${
                  sel ? "border-cyan-400/50 bg-cyan-400/10" : "border-white/10 hover:border-white/25"
                }`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-white">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.exam} · #{c.nirf_rank}</p>
                </div>
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${sel ? "border-cyan-400 bg-cyan-400 text-[#041016]" : "border-white/20 text-slate-500"}`}>
                  {sel ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Comparison table */}
      {compared && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-[#0B0F17] p-4 text-left text-xs uppercase tracking-wide text-slate-500 font-medium">Parameter</th>
                {compared.map((c) => (
                  <th key={c.id} data-testid="college-compare-col" className="p-4 text-left min-w-[160px]">
                    <p className="font-display font-semibold text-white">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.city}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PARAMS.map((p, idx) => {
                const bestVal = best(p.key, p.better);
                return (
                  <tr key={p.key} className={idx % 2 ? "bg-white/[0.02]" : ""}>
                    <td className="sticky left-0 bg-[#0B0F17] p-4 text-sm font-medium text-slate-300">{p.label}</td>
                    {compared.map((c) => {
                      const isBest = bestVal !== null && c[p.key] === bestVal;
                      return (
                        <td key={c.id} data-testid={isBest ? "compare-difference-highlight" : undefined}
                          className={`p-4 text-sm font-mono-data ${isBest ? "text-emerald-300 font-bold" : "text-slate-300"}`}>
                          {p.fmt(c[p.key])} {isBest && <span className="text-emerald-400">✓</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </motion.div>
      )}
    </div>
  );
}
